/**
 * @fileoverview State, effects, and handlers for the company profile Contact Information section.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyFieldValue } from "../../../utils/common";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { useContactInformationFieldState } from "./useContactInformationFieldState";

function useContactInformationTablet() {
  // Detect tablet view for CountryCodeSelector width
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1023);
    };

    checkTablet();
    window.addEventListener("resize", checkTablet);

    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  return isTablet;
}

function buildContactValidationDefaults(company, selectedProfile) {
  return {
    telefonoDeOficina: getCompanyFieldValue(
      company,
      "telefonoDeOficina",
      selectedProfile,
      "",
    ),
    extensionTelefono: getCompanyFieldValue(
      company,
      "extensionTelefono",
      selectedProfile,
      "",
    ),
    telefonoSecundario: getCompanyFieldValue(
      company,
      "telefonoSecundario",
      selectedProfile,
      "",
    ),
    correoElectronicoPrincipal: getCompanyFieldValue(
      company,
      "correoElectronicoPrincipal",
      selectedProfile,
      "",
    ),
    paginaInternet: getCompanyFieldValue(
      company,
      "paginaInternet",
      selectedProfile,
      "",
    ),
  };
}

function useContactInformationValidation(hasErrors, onValidationChange) {
  const prevHasErrorsRef = useRef(false);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);
}

/**
 * Handles field value changes by notifying the parent component.
 * @param {Function} onFieldChange - Parent callback triggered when a field value changes
 * @param {string} field - The field name being changed
 * @param {*} value - The new value for the field
 */
function notifyFieldChange(onFieldChange, field, value) {
  if (onFieldChange) {
    onFieldChange(field, value);
  }
}

export function useContactInformationSection({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) {
  const { t } = useTranslation();
  const isTablet = useContactInformationTablet();
  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation(
    buildContactValidationDefaults(company, selectedProfile),
  );
  const fields = useContactInformationFieldState({
    company,
    selectedProfile,
    handleFieldChange,
  });

  const handleInputChange = (field, value) => {
    notifyFieldChange(onFieldChange, field, value);
  };

  // Handle validated field changes
  const handleValidatedFieldChange = (fieldName, value) => {
    handleFieldChange(fieldName, value);
    handleInputChange(fieldName, value);
  };

  // handleToggleChange uses the same implementation as handleInputChange
  const handleToggleChange = handleInputChange;

  useContactInformationValidation(hasErrors, onValidationChange);

  return {
    t,
    isTablet,
    getErrorMessage,
    getFieldValidationState,
    handleInputChange,
    handleValidatedFieldChange,
    handleToggleChange,
    ...fields,
  };
}
